import { snakeCaseObject } from '@edx/frontend-platform';

import { LETTER_REGEX, NUMBER_REGEX } from '../../data/constants';
import messages from '../messages';
import validateCompany from '../RegistrationFields/CompanyField/validator';
import validateEmail from '../RegistrationFields/EmailField/validator';
import validateJobTitle from '../RegistrationFields/JobTitleField/validator';
import validateName from '../RegistrationFields/NameField/validator';
import validateProfessionalNumber from '../RegistrationFields/ProfessionalNumberField/validator';
import validateUsername from '../RegistrationFields/UsernameField/validator';

/**
 * It validates the password field value
 * @param value
 * @param formatMessage
 * @returns {string}
 */
export const validatePasswordField = (value, formatMessage) => {
  let fieldError = '';
  if (!value || !LETTER_REGEX.test(value) || !NUMBER_REGEX.test(value) || value.length < 8) {
    fieldError = formatMessage(messages['password.validation.message']);
  }
  return fieldError;
};

/**
 * It accepts complete registration data as payload and checks if the form is valid.
 * @param payload
 * @param errors
 * @param configurableFormFields
 * @param fieldDescriptions
 * @param formatMessage
 * @returns {{fieldErrors, isValid: boolean}}
 */
export const isFormValid = (
  payload,
  errors,
  configurableFormFields,
  fieldDescriptions,
  formatMessage,
) => {
  const safeFieldDescriptions = fieldDescriptions || {};
  const fieldErrors = { ...errors };
  let isValid = true;
  let emailSuggestion = { suggestion: '', type: '' };
  const currentYear = new Date().getFullYear();

  Object.keys(payload).forEach(key => {
    switch (key) {
    case 'name':
      if (!fieldErrors.name) {
        fieldErrors.name = validateName(payload.name, formatMessage);
      }
      if (fieldErrors.name) { isValid = false; }
      break;
    case 'email': {
      if (!fieldErrors.email) {
        const {
          fieldError, confirmEmailError, suggestion,
        } = validateEmail(payload.email, configurableFormFields?.confirm_email, formatMessage);
        if (fieldError) {
          fieldErrors.email = fieldError;
          isValid = false;
        }
        if (confirmEmailError) {
          fieldErrors.confirm_email = confirmEmailError;
          isValid = false;
        }
        emailSuggestion = suggestion;
      }
      if (fieldErrors.email) { isValid = false; }
      break;
    }
    case 'username':
      if (!fieldErrors.username) {
        fieldErrors.username = validateUsername(payload.username, formatMessage);
      }
      if (fieldErrors.username) { isValid = false; }
      break;
    case 'password':
      if (!fieldErrors.password) {
        fieldErrors.password = validatePasswordField(payload.password, formatMessage);
      }
      if (fieldErrors.password) { isValid = false; }
      break;
    case 'company':
      if (!fieldErrors.company) {
        fieldErrors.company = validateCompany(payload.company, formatMessage);
      }
      if (fieldErrors.company) { isValid = false; }
      break;
    case 'job_title':
      if (!fieldErrors.job_title) {
        fieldErrors.job_title = validateJobTitle(payload.job_title, formatMessage);
      }
      if (fieldErrors.job_title) { isValid = false; }
      break;
    case 'professional_number':
      if (!fieldErrors.professional_number) {
        fieldErrors.professional_number = validateProfessionalNumber(payload.professional_number, formatMessage);
      }
      if (fieldErrors.professional_number) { isValid = false; }
      break;
    default:
      break;
    }
  });

  // Ensure required custom fields are always validated, even if missing from a stale payload shape.
  if (!Object.prototype.hasOwnProperty.call(payload, 'company')) {
    fieldErrors.company = validateCompany('', formatMessage);
    if (fieldErrors.company) { isValid = false; }
  }
  if (!Object.prototype.hasOwnProperty.call(payload, 'job_title')) {
    fieldErrors.job_title = validateJobTitle('', formatMessage);
    if (fieldErrors.job_title) { isValid = false; }
  }
  if (!Object.prototype.hasOwnProperty.call(payload, 'professional_number')) {
    fieldErrors.professional_number = validateProfessionalNumber('', formatMessage);
    if (fieldErrors.professional_number) { isValid = false; }
  }

  // Don't validate when country field is optional or hidden and not present on registration form
  if (configurableFormFields?.country && !configurableFormFields.country?.displayValue) {
    fieldErrors.country = formatMessage(messages['empty.country.field.error']);
    isValid = false;
  } else if (configurableFormFields?.country && !configurableFormFields.country?.countryCode) {
    fieldErrors.country = formatMessage(messages['invalid.country.field.error']);
    isValid = false;
  }

  Object.keys(safeFieldDescriptions).forEach(key => {
    // Skip fields already validated in the main payload flow (e.g. company/job_title).
    // This avoids overwriting existing field errors with backend dynamic-field metadata.
    if (Object.prototype.hasOwnProperty.call(payload, key)) {
      return;
    }

    if (key === 'year_of_birth') {
      const rawValue = configurableFormFields?.year_of_birth;
      const year = rawValue ? parseInt(rawValue, 10) : NaN;
      if (!year || Number.isNaN(year)) {
        fieldErrors[key] = safeFieldDescriptions[key].error_message;
      } else if ((currentYear - year) < 13) {
        fieldErrors[key] = formatMessage(messages['registration.year.of.birth.error']);
      }
      if (fieldErrors[key]) { isValid = false; }
      return;
    }
    if (key === 'country' && !configurableFormFields?.country?.displayValue) {
      fieldErrors[key] = formatMessage(messages['empty.country.field.error']);
    } else if (!configurableFormFields[key]) {
      fieldErrors[key] = safeFieldDescriptions[key].error_message;
    }
    if (fieldErrors[key]) { isValid = false; }
  });

  return { isValid, fieldErrors, emailSuggestion };
};

/**
 * It prepares a payload for registration data that can be passed to registration API endpoint.
 * @param initPayload
 * @param configurableFormFields
 * @param showMarketingEmailOptInCheckbox
 * @param totalRegistrationTime
 * @param queryParams
 * @returns {*}
 */
export const prepareRegistrationPayload = (
  initPayload,
  configurableFormFields,
  showMarketingEmailOptInCheckbox,
  totalRegistrationTime,
  queryParams,
) => {
  let payload = { ...initPayload };
  Object.keys(configurableFormFields).forEach((fieldName) => {
    if (fieldName === 'country') {
      payload[fieldName] = configurableFormFields[fieldName].countryCode;
    } else {
      payload[fieldName] = configurableFormFields[fieldName];
    }
  });

  // Don't send the marketing email opt-in value if the flag is turned off
  if (!showMarketingEmailOptInCheckbox) {
    delete payload.marketingEmailsOptIn;
  }

  // Custom backend requires `profession`; map from `job_title` when present.
  if (!payload.profession && payload.job_title) {
    payload.profession = payload.job_title;
  }

  // Drop empty values to avoid backend validation errors from blank optional fields.
  Object.entries(payload).forEach(([key, value]) => {
    if (value === '' || value === null || value === undefined) {
      delete payload[key];
    }
  });

  payload.totalRegistrationTime = totalRegistrationTime;
  payload = snakeCaseObject(payload);

  // add query params to the payload
  payload = { ...payload, ...queryParams };
  return payload;
};
